import { Component, DestroyRef, ElementRef, inject, OnInit, PLATFORM_ID, signal, computed, viewChild } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ThaiDatePipe } from '../../pipes/thai-date.pipe';
import { FormsModule } from '@angular/forms';
import { ChatService } from '../../../core/services/chat.service';
import { FloatingChatService } from '../../../core/services/floating-chat.service';
import { SignalRService } from '../../../core/services/signalr.service';
import { ChatMessage, ChatThread, PeerReadInfo } from '../../../models/chat.model';
import { AuthService } from '../../../core/services/auth.service';
import { StoreVerifiedBadge } from '../store-verified-badge/store-verified-badge';
import { UserAvatar } from '../user-avatar/user-avatar';
import { environment } from '../../../../environments/environment';
import { debounceTime, Subject } from 'rxjs';

@Component({
  selector: 'app-floating-chat',
  standalone: true,
  imports: [FormsModule, ThaiDatePipe, StoreVerifiedBadge, UserAvatar],
  templateUrl: './floating-chat.html',
  styleUrl: './floating-chat.css',
})
export class FloatingChat implements OnInit {
  private readonly chatSvc    = inject(ChatService);
  private readonly floatingChatSvc = inject(FloatingChatService);
  private readonly signalr    = inject(SignalRService);
  private readonly authSvc    = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformId = inject(PLATFORM_ID);

  readonly isOpen      = signal(false);
  readonly threads     = signal<ChatThread[]>([]);
  readonly activeThreadId = signal<string | null>(null);
  readonly messages    = signal<ChatMessage[]>([]);
  readonly newMessage  = signal('');
  readonly unreadTotal = computed(() => this.threads().reduce((s, t) => s + t.unread_count, 0));
  readonly fileUrl = environment.file;
  readonly fileLocalUrl = environment.fileLocal;
  private readonly msgContainer = viewChild<ElementRef>('fcMsgContainer');

  /** Typing indicator */
  readonly peerIsTyping = signal(false);
  readonly peerTypingName = signal('');
  private typingTimeout: any = null;
  private readonly typingInput$ = new Subject<void>();

  /** Read receipt */
  readonly peerReadInfo = signal<PeerReadInfo>({ last_read_at: null, avatar_url: null, display_name: null });

  /** Current user's role to determine message alignment */
  /** floating-chat ใช้เฉพาะมุมมอง bidder เสมอ (แม้ user จะเป็น owner ก็ตาม เพราะ owner มี /owner/chat แยก) */
  readonly myRole = computed(() => 'bidder' as const);

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    this.chatSvc.getMyThreads('bidder').subscribe(t => this.threads.set(t));

    // ฟังคำสั่งเปิด chat จาก component อื่น
    const openSub = this.floatingChatSvc.openThread$.subscribe(threadId => {
      this.isOpen.set(true);
      this.openThread(threadId);
    });
    this.destroyRef.onDestroy(() => openSub.unsubscribe());

    // ฟัง real-time chat messages (ป้องกัน duplicate ด้วย id)
    const sub = this.signalr.chatMessage$.subscribe(msg => {
      const activeId = this.activeThreadId();
      const myUserId = this.authSvc.currentUser()?.userId ?? '';
      const isMyMsg = !!myUserId && msg.sender_user_id === myUserId;

      if (msg.thread_id === activeId) {
        this.messages.update(list =>
          list.some(m => m.id === msg.id) ? list : [...list, msg]
        );
        this.scrollToBottom();
        // mark read ทันทีเมื่อได้รับข้อความใหม่ใน thread ที่เปิดอยู่
        if (!isMyMsg) this.signalr.sendMarkRead(activeId);
      } else if (!isMyMsg) {
        // เพิ่ม unread เฉพาะข้อความของอีกฝ่าย ที่ thread ไม่ได้เปิดอยู่
        this.threads.update(list =>
          list.map(t => t.thread_id === msg.thread_id
            ? { ...t, last_message: msg.message, last_message_at: msg.created_at, unread_count: t.unread_count + 1 }
            : t
          )
        );
      }
      // อัพเดต last_message และเรียงลำดับ
      this.threads.update(list =>
        list.map(t => t.thread_id === msg.thread_id
          ? { ...t, last_message: msg.message, last_message_at: msg.created_at }
          : t
        ).sort((a, b) => {
          if (!a.last_message_at && !b.last_message_at) return 0;
          if (!a.last_message_at) return 1;
          if (!b.last_message_at) return -1;
          return new Date(b.last_message_at).getTime() - new Date(a.last_message_at).getTime();
        })
      );
    });
    this.destroyRef.onDestroy(() => sub.unsubscribe());

    // ฟัง typing event
    const typingSub = this.signalr.userTyping$.subscribe(evt => {
      if (evt.thread_id === this.activeThreadId()) {
        this.peerIsTyping.set(true);
        this.peerTypingName.set(evt.is_owner && evt.company_name ? evt.company_name : evt.display_name);
        this.scrollToBottom();
        clearTimeout(this.typingTimeout);
        this.typingTimeout = setTimeout(() => this.peerIsTyping.set(false), 5000);
      }
    });
    this.destroyRef.onDestroy(() => typingSub.unsubscribe());

    // ฟัง read receipt
    const readSub = this.signalr.threadRead$.subscribe(evt => {
      if (evt.thread_id === this.activeThreadId()) {
        this.peerReadInfo.set({ last_read_at: evt.read_at, avatar_url: evt.avatar_url, display_name: evt.display_name });
        this.scrollToBottom();
      }
    });
    this.destroyRef.onDestroy(() => readSub.unsubscribe());

    // Debounce typing input → ส่ง SignalR ทุก 500ms
    const typingInputSub = this.typingInput$.pipe(debounceTime(200)).subscribe(() => {
      const tid = this.activeThreadId();
      if (tid) this.signalr.sendTyping(tid);
    });
    this.destroyRef.onDestroy(() => typingInputSub.unsubscribe());
  }

  toggle(): void {
    const next = !this.isOpen();
    this.isOpen.set(next);
    if (next) {
      this.refreshThreads();
    } else {
      // ปิด popup → ออกจาก thread ที่เปิดอยู่ เพื่อไม่ให้ mark read ข้อความใหม่โดยอัตโนมัติ
      const prev = this.activeThreadId();
      if (prev) {
        this.signalr.sendMarkRead(prev);
        this.signalr.leaveThread(prev);
      }
      this.activeThreadId.set(null);
    }
  }

  async back(): Promise<void> {
    const prev = this.activeThreadId();
    if (prev) {
      await this.signalr.sendMarkRead(prev);
      this.signalr.leaveThread(prev);
    }
    this.activeThreadId.set(null);
    // หน่วงเวลาเล็กน้อยเพื่อให้แน่ใจว่า MarkRead ในระดับ DB เสร็จแล้วป้องกัน Race condition
    setTimeout(() => this.refreshThreads(), 500);
  }

  /** โหลด threads ใหม่เพื่อ sync unread count จาก server */
  private refreshThreads(): void {
    this.chatSvc.getMyThreads('bidder').subscribe(t => this.threads.set(t));
  }

  openThread(threadId: string): void {
    const prev = this.activeThreadId();
    if (prev) this.signalr.leaveThread(prev);

    this.activeThreadId.set(threadId);
    this.signalr.joinThread(threadId);

    // reset unread สำหรับ thread นี้
    this.threads.update(list =>
      list.map(t => t.thread_id === threadId ? { ...t, unread_count: 0 } : t)
    );

    this.peerIsTyping.set(false);
    this.chatSvc.getMessages(threadId).subscribe(async res => {
      this.messages.set(res.messages);
      this.peerReadInfo.set(res.peer_read_info);
      this.scrollToBottom();
      await this.signalr.sendMarkRead(threadId);
      // หน่วงเวลาให้ Server บันทึก MarkRead เผื่อ DB ทำงานช้า
      setTimeout(() => this.refreshThreads(), 500);
    });
  }

  send(): void {
    const msg = this.newMessage().trim();
    const threadId = this.activeThreadId();
    if (!msg || !threadId) return;
    this.newMessage.set('');
    this.chatSvc.sendMessage(threadId, msg).subscribe(sent => {
      // เพิ่มข้อความที่ส่งสำเร็จเข้า UI ทันที (ป้องกัน duplicate ด้วย id)
      this.messages.update(list =>
        list.some(m => m.id === sent.id) ? list : [...list, sent]
      );
      this.scrollToBottom();
      this.threads.update(list =>
        list.map(t => t.thread_id === threadId
          ? { ...t, last_message: sent.message, last_message_at: sent.created_at }
          : t
        )
      );
    });
  }

  onNewMessage(value: string): void {
    this.newMessage.set(value);
    this.typingInput$.next();
  }

  activePeerName(): string {
    const t = this.threads().find(th => th.thread_id === this.activeThreadId());
    if (!t) return '';
    return this.peerName(t);
  }

  /** true = peer (ร้านค้า) verified KYC แล้ว — แสดง badge เฉพาะเมื่อ viewer เป็น bidder (ดูชื่อร้าน) */
  activeThreadVerified(): boolean {
    const t = this.threads().find(th => th.thread_id === this.activeThreadId());
    if (!t || this.myRole() !== 'bidder') return false;
    return !!t.peer_is_verified;
  }

  /** true = thread นี้ (peer = ร้าน) verified KYC แล้ว — ใช้ใน thread list ฝั่ง bidder */
  threadVerified(t: ChatThread): boolean {
    return this.myRole() === 'bidder' && !!t.peer_is_verified;
  }

  /** แสดงชื่อ peer ตาม role — bidder เห็นชื่อร้าน, owner เห็นชื่อ bidder */
  peerName(t: ChatThread): string {
    return (this.myRole() === 'bidder' && t.company_name) ? t.company_name : t.peer_display_name;
  }

  /** แปลง avatar url — ถ้าเป็น URL เต็มใช้ตรง, ถ้า relative ต่อ fileUrl */
  /** แปลง avatar url — URL เต็มใช้ตรง, relative ใช้ fileLocal (local server) */
  avatarSrc(url: string | null | undefined): string {
    if (!url) return '';
    return url.startsWith('http') ? url : this.fileLocalUrl + url;
  }

  /** ตรวจว่า message นี้เป็นข้อความสุดท้ายที่ peer อ่านถึง */
  isLastReadByPeer(msgId: string): boolean {
    const readAt = this.peerReadInfo().last_read_at;
    if (!readAt) return false;
    const readMsgs = this.messages().filter(m => new Date(m.created_at) <= new Date(readAt));
    if (readMsgs.length === 0) return false;
    return readMsgs[readMsgs.length - 1].id === msgId;
  }

  /** เลื่อน message container ลงล่างสุด */
  private scrollToBottom(): void {
    setTimeout(() => {
      const el = this.msgContainer()?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    }, 50);
  }
}
